import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  firstValueFrom,
  forkJoin,
  interval,
  lastValueFrom,
  map,
  of,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  CaseTypeEnum,
  FeaRegion,
} from '../../common/constants/temporary-constant';
import { CaseDamageDetailDto } from '../../common/api/models/caseDamageDetailDto';
import { ContainerMoveDto } from '../../common/api/models/containerMoveDto';
import { FormValidation } from '../../common/api/models/formValidation';
import { ISharedRecoveryCaseService } from '../../shared-recovery-case-service';
import { TemplateModel } from '@maersk-global/angular-shared-library';
import {
  BlobService,
  CaseDamageDetailsService,
  FeaPreService,
  FileUploadService,
  InvoiceDetailService,
  LiabilityLetterService,
  LiabilityPartyService,
  NotificationService,
  RecoveryCaseDocumentService,
  RecoveryCaseService,
  RecoveryInspectionService,
} from '../../common/api';
import { RecoveryRequest } from '../../common/api/models/recovery-request';
import { CaseDamageDetails } from '../../common/api/models/caseDamageDetails';
import { SharedDataService } from '../../shared-data-service';
import { DcrpAuthorizationService } from '../../common/services/authorization/dcrp-authorization.service';
import { ClaimReferenceDocumentDto } from '../../common/api/models/claimReferenceDocumentDto';
import { Inspection } from '../../common/api/models/inspection';
import { InspectionDetailsDTO } from '../../common/api/models/inspectionDetailsDTO';
import { environment } from '../../../environments/environment';
import { CaseActivityLogsDtoListResponse } from '../../common/api/models/caseActivityLogsDtoListResponse';
import { RecoveryCaseType } from '../../common/api/models/recoveryCaseType';
import { LiabilityLetter } from '../../common/api/models/liabilityLetter';
import { LiabilityParty } from '../../common/api/models/liabilityParty';
import { LiabilityLetterStatus } from '../../common/api/models/liabilityLetterStatus';
import { UpdateLiabilityLetterStatusVendor } from '../../common/api/models/updateLiabilityLetterStatusVendor';
import { SendMailRequest } from '../../common/api/models/sendMailRequest';
import { SendGridEmail } from '../../common/api/models/email';
import { InvoiceDetail } from '../../common/api/models/invoiceDetail';
import { UpdateInvoice } from '../../common/api/models/updateInvoice';
import { CreateInvoice } from '../../common/api/models/createInvoice';
import { InvoicingStatus } from '../../common/api/models/invoicingStatus';
import { CustomerInfoDto } from '../../common/api/models/customerInfoDto';
import { gcssBookingInfo } from '../../common/api/models/gcssBookingInfo';
import { SaveLiabilityLetter } from '../../common/api/models/saveLiabilityLetter';
import { UploadEmailRequest } from '../../common/api/models/uploadEmailRequest';
import { CurrencyAndExchangeRateDto } from '../../common/api/models/currencyAndExchangeRateDto';
import { RecoveryCaseOverview } from '../../common/api/models/recoveryCaseOverview';
import { FeaRecoveryCase } from '../../common/api/models/feaRecovery';
import { FeaRecoverableCostsSummary } from '../../common/api/models/feaRecoverableCostsSummary';
import { RecoveryCase } from '../../common/api/models/recoveryCase';
import { FeaPreEpTradeModel } from '../../common/api/models/feaPreEpTradeModel';
@Injectable({
  providedIn: 'root',
})
export class SharedFeaRecoveryCaseService
  implements ISharedRecoveryCaseService
{
  private apiVersion: string = '1.0';
  caseType: CaseTypeEnum | undefined;
  PRS_REGION: string = 'PRS';
  PRN_REGION: string = 'PRN';
  PRE_REGION: string = 'PRE';
  PRS_COMPLETE_STAGE: number = 4;
  PRN_COMPLETE_STAGE: number = 5;
  PRE_COMPLETE_STAGE: number = 6;
  constructor(
    private _route: ActivatedRoute,
    private _recoveryClaimService: RecoveryCaseService,
    private _damageService: CaseDamageDetailsService,
    private _sharedDataService: SharedDataService,
    private _dcrpAuthorizationService: DcrpAuthorizationService,
    private _recoveryCaseDocumentService: RecoveryCaseDocumentService,
    private _recoveryInspectionService: RecoveryInspectionService,
    private _liabilityLetterService: LiabilityLetterService,
    private _liabilityPartyService: LiabilityPartyService,
    private _notificationService: NotificationService,
    private _invoiceDetailService: InvoiceDetailService,
    private _liabilityService: LiabilityLetterService,
    private _blobService: BlobService,
    private _feaPreService: FeaPreService,
    private _fileUploadService: FileUploadService
  ) {}
  updateEirImageLastFetchOn(_date: Date | undefined): void {
    throw new Error('Method not implemented.');
  }
  updateWorkOrderDate(_date: Date | null | undefined): void {
    throw new Error('Method not implemented.');
  }

  private shouldShowLiabilityDetailsSubject$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private tabIndex$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private reloadDamageDetails$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private currentStageId$$: BehaviorSubject<number> =
    new BehaviorSubject<number>(1);

  private reloadFeaRecoveryData$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private formValidity$$: BehaviorSubject<FormValidation | null> =
    new BehaviorSubject<FormValidation | null>(null);

  private containerDetails$$: BehaviorSubject<
    ContainerMoveDto | null | undefined
  > = new BehaviorSubject<ContainerMoveDto | null | undefined>(undefined);

  private overviewDetails$$: BehaviorSubject<TemplateModel[]> =
    new BehaviorSubject<TemplateModel[]>([]);

  private reloadLiabilityLetters$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private reloadLiabilityPartyDetails$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private workFlowStageId$$: BehaviorSubject<number> =
    new BehaviorSubject<number>(1);

  workFlowStageId$: Observable<number> = this.workFlowStageId$$.asObservable();

  private reloadInvoices$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private reloadBulkInvoices$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private shouldShowIssueInvoiceScreenSubject$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private customerDataSubject$$: BehaviorSubject<CustomerInfoDto> =
    new BehaviorSubject<CustomerInfoDto>({});

  private caseCurrencyAndExchangeRate$$: BehaviorSubject<
    CurrencyAndExchangeRateDto | undefined
  > = new BehaviorSubject<CurrencyAndExchangeRateDto | undefined>(undefined);

  private selectedFactCode$$: BehaviorSubject<string | undefined> =
    new BehaviorSubject<string | undefined>(undefined);

  private total3pCost$$: BehaviorSubject<number> = new BehaviorSubject<number>(
    0
  );

  private reloadEpTradeUploadedFilesSubject$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  total3pCost$: Observable<number> = this.total3pCost$$.asObservable();

  caseCurrencyAndExchangeRate$: Observable<
    CurrencyAndExchangeRateDto | undefined
  > = this.caseCurrencyAndExchangeRate$$.asObservable();

  feaRecoveryCustomerData$: Observable<CustomerInfoDto> =
    this.customerDataSubject$$.asObservable().pipe(shareReplay(1));

  private unSelectedLinkedCases$$: BehaviorSubject<number[]> =
    new BehaviorSubject<number[]>([]);

  unSelectedLinkedCases$: Observable<number[]> =
    this.unSelectedLinkedCases$$.asObservable();

  private selectedLinkedCases$$: BehaviorSubject<number[]> =
    new BehaviorSubject<number[]>([]);

  selectedLinkedCases$: Observable<number[]> =
    this.selectedLinkedCases$$.asObservable();

  selectedFactCode$: Observable<string | undefined> =
    this.selectedFactCode$$.asObservable();

  private linkedCases$$: BehaviorSubject<RecoveryCase[]> = new BehaviorSubject<
    RecoveryCase[]
  >([]);

  linkedCases$: Observable<RecoveryCase[]> = this.linkedCases$$.asObservable();

  reloadEpTradeDataSubject$$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  reloadEpTradeData$: Observable<boolean> =
    this.reloadEpTradeDataSubject$$.asObservable();

  feaRecoveryCaseData$: Observable<FeaRecoveryCase | undefined> = combineLatest(
    [this._route.queryParamMap, this.reloadFeaRecoveryData$$.asObservable()]
  ).pipe(
    switchMap(([params, reload]) => {
      const caseType = params.get('type');
      if (caseType != 'fea') return of(undefined);
      const caseId = params.get('caseId');
      if (!caseId) return of(undefined);
      const request = {
        caseId: parseInt(caseId),
        recoveryCaseType: 'Fea',
      } as RecoveryRequest;
      return this._recoveryClaimService.recoveryCasesGet(request).pipe(
        map((response) => {
          const data = response.data?.at(0);
          if (!reload || data?.recoveryStatusId == 1) {
            const currentStageId =
              !data || !data.workflowStageId ? 1 : data.workflowStageId;
            this.updateCurrentStageId(currentStageId);
          }
          return response.data?.at(0);
        })
      );
    }),
    tap((recoveryData) => {
      if (!recoveryData) return;
      this.workFlowStageId$$.next(recoveryData?.workflowStageId ?? 1);
      const caseCurrencyAndExchangeRate = {
        quotationCurrency: recoveryData.caseCurrency,
      } as CurrencyAndExchangeRateDto;
      this.caseCurrencyAndExchangeRate$$.next(caseCurrencyAndExchangeRate);
    }),
    shareReplay(1)
  );

  currentStageId$: Observable<number> = this.currentStageId$$.asObservable();

  currentFeaRecoveryTabIndex$: Observable<number | 0> =
    this.tabIndex$$.asObservable();

  overviewDetails$: Observable<TemplateModel[]> =
    this.overviewDetails$$.asObservable();

  feaRecoveryLiabilityLetters$: Observable<Array<LiabilityLetter> | undefined> =
    combineLatest([
      this.feaRecoveryCaseData$,
      this.reloadLiabilityLetters$$.asObservable(),
    ]).pipe(
      switchMap(([feaRecovery]) => {
        if (!feaRecovery || !feaRecovery.id) return of(undefined);
        return this._liabilityLetterService
          .liabilityLetterGetAllLiabilityLettersRecoveryCaseIdGet(
            feaRecovery.id,
            this.apiVersion
          )
          .pipe(map((response) => response));
      }),
      shareReplay(1)
    );

  feaRecoveryInvoices$: Observable<Array<InvoiceDetail> | undefined> =
    combineLatest([
      this.feaRecoveryCaseData$,
      this.reloadInvoices$$.asObservable(),
      this.reloadBulkInvoices$$.asObservable(),
    ]).pipe(
      switchMap(([feaRecoveryCase]) => {
        if (!feaRecoveryCase || !feaRecoveryCase.id) return of(undefined);

        // check if the case Fea PRE region case
        const isFeaPreRegionCase = feaRecoveryCase.region === 'PRE';
        console.log('isFeaPreRegionCase', isFeaPreRegionCase);
        if (isFeaPreRegionCase) {
          return this._invoiceDetailService
            .invoiceDetailsSearchGet(
              this.apiVersion,
              undefined,
              feaRecoveryCase.id
            )
            .pipe(
              map((response) => response.data),
              catchError(() => of(undefined))
            );
        } else {
          return this._invoiceDetailService
            .invoiceDetailsSearchGet(this.apiVersion, feaRecoveryCase.id)
            .pipe(
              map((response) => response.data),
              catchError(() => of(undefined))
            );
        }
      }),
      shareReplay(1)
    );

  shouldShowFeaRecoveryIssueInvoiceScreen$: Observable<boolean> = combineLatest(
    [
      this.shouldShowIssueInvoiceScreenSubject$$.asObservable(),
      this.feaRecoveryInvoices$,
      this._sharedDataService.countries$,
      this.feaRecoveryCaseData$,
    ]
  ).pipe(
    map(
      ([shouldShowIssueInvoiceScreen, invoices, countries, feaRecoveryCase]) =>
        (shouldShowIssueInvoiceScreen || !invoices || invoices.length === 0) &&
        !!countries.find(
          (country) =>
            country.code === feaRecoveryCase?.responsibleCountryCode &&
            country.isS4HanaMigrated
        )
    ),
    shareReplay(1)
  );

  linkedCasesSummary$: Observable<FeaRecoverableCostsSummary | undefined> =
    combineLatest([this.feaRecoveryCaseData$]).pipe(
      switchMap(([feaRecoveryCase]) => {
        if (!feaRecoveryCase || !feaRecoveryCase.id) return of(undefined);
        return this._recoveryClaimService
          .recoveryCasesLinkedCasesRecoverableCostSummaryGet(
            RecoveryCaseType.Fea,
            1,
            20,
            undefined,
            undefined,
            feaRecoveryCase.id,
            undefined,
            undefined
          )
          .pipe(
            map((response) => response),
            catchError(() => of(undefined))
          );
      })
    );

  updateTotal3pCost(totalCost: number): void {
    this.total3pCost$$.next(totalCost);
  }

  private invoiceCreationPermissionInitial$: Observable<InvoicingStatus> =
    combineLatest([
      this.feaRecoveryCaseData$,
      this.feaRecoveryLiabilityLetters$,
    ]).pipe(
      switchMap(([feaRecoveryCase, letters]) => {
        if (
          !letters ||
          letters.length === 0 ||
          !feaRecoveryCase?.id ||
          !letters.find(
            (letter) =>
              letter.liabilityLetterStatus === 'ACCEPTED' ||
              letter.liabilityLetterStatus === 'NO_RESPONSE'
          )
        )
          return of({
            totalSecondsRemainingToInvoice: 0,
            isReadyToInvoice: false,
          });
        // TODO: hardcoded the time duration for ready to invoice for now.
        // We have to check whether to call API for this same as customer recovery or not.
        return of({
          totalSecondsRemainingToInvoice: 0,
          isReadyToInvoice: true,
        });
      }),
      shareReplay(1)
    );

  feaRecoveryInvoiceCreationPermission$: Observable<
    InvoicingStatus & { displayMessage: string }
  > = combineLatest([
    this.invoiceCreationPermissionInitial$,
    this.invoiceCreationPermissionInitial$.pipe(
      switchMap((caseInvoiceStatus) => {
        return interval(1000).pipe(
          take((caseInvoiceStatus.totalSecondsRemainingToInvoice ?? 1) + 1)
        );
      })
    ),
  ]).pipe(
    map(([caseInvoiceStatus, seconds]) => {
      const displayMessage =
        this._sharedDataService.convertSecondsToDayHourMinSec(
          (caseInvoiceStatus.totalSecondsRemainingToInvoice ?? 1) - seconds
        );
      if (
        !caseInvoiceStatus.isReadyToInvoice &&
        seconds < (caseInvoiceStatus.totalSecondsRemainingToInvoice ?? 1)
      )
        return {
          totalSecondsRemainingToInvoice:
            caseInvoiceStatus.totalSecondsRemainingToInvoice,
          isReadyToInvoice: caseInvoiceStatus.isReadyToInvoice,
          displayMessage: displayMessage,
        };
      else
        return {
          totalSecondsRemainingToInvoice: 0,
          isReadyToInvoice:
            caseInvoiceStatus.isReadyToInvoice ||
            caseInvoiceStatus.totalSecondsRemainingToInvoice !== 0,
          displayMessage: displayMessage,
        };
    })
  );

  enableCloseBtn$: Observable<boolean> = combineLatest([
    this.feaRecoveryCaseData$,
    this.currentStageId$,
    this.feaRecoveryInvoices$,
  ]).pipe(
    map(
      ([recoveryData, stageId, invoices]) =>
        !(
          // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
          (
            recoveryData?.caseStatus == 6 ||
            recoveryData?.caseStatus == 7 ||
            stageId == 4 ||
            (invoices && invoices.length > 0)
          )
        )
    )
  );

  reopenCase$: Observable<boolean> = this.feaRecoveryCaseData$.pipe(
    map((recoveryData) => {
      // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
      return recoveryData?.caseStatus == 6 || recoveryData?.caseStatus == 7;
    })
  );

  disableForm$: Observable<boolean> = combineLatest([
    this._dcrpAuthorizationService.isAuthorizedFor('recovery-update'),
    this.feaRecoveryLiabilityLetters$,
    this.feaRecoveryCaseData$,
    this.currentStageId$,
    this.feaRecoveryInvoices$,
  ]).pipe(
    map(([isAuthorizedForRecoveryUpdate, letters, recoveryData, stageId, invoices]) => {
      if (
        !isAuthorizedForRecoveryUpdate ||
        // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
        recoveryData?.caseStatus == 6 ||
        recoveryData?.caseStatus == 7
      )
        return true;
      
      // Disable previous stages' forms when invoice details are present
      if (invoices && invoices.length > 0) {
        const region = recoveryData?.region;
        let invoiceStageId: number;
        
        // Determine invoice stage ID based on region
        if (region === 'PRE') {
          invoiceStageId = 5; // Invoice stage for PRE region
        } else {
          invoiceStageId = 0; // Unknown region, don't disable
        }
        
        // Disable all stages before the invoice stage when invoices exist
        if (invoiceStageId > 0 && stageId < invoiceStageId) {
          return true;
        }
      }
      
      if (stageId == 1 && letters && letters.length > 0) {
        letters?.sort(
          (letter1, letter2) =>
            new Date(letter2.createdTimestamp ?? '').getTime() -
            new Date(letter1.createdTimestamp ?? '').getTime()
        );

        return letters[0].liabilityLetterStatus ===
          LiabilityLetterStatus.REJECTED
          ? false
          : true;
      }
      return false;
    })
  );

  /**
   * We are calculating the accumulated state of the form validity. For e.g. If out of 2 forms 1 is valid and 1 is invalid,
   * the accumulated state would be invalid.
   */
  private collectiveFormValidation$ = this.formValidity$$.asObservable().pipe(
    withLatestFrom(this.currentStageId$, this.feaRecoveryCaseData$),
    map(([formValidation, currentStageId, feaRecoveryData]) =>
      formValidation
        ? {
            ...formValidation,
            stage: currentStageId,
            caseId: feaRecoveryData?.id,
          }
        : null
    ),
    scan((formValidations, currentState) => {
      if (!currentState) return [];
      const matchingState = formValidations.find(
        (state) =>
          state.component === currentState.component &&
          state.stage === currentState.stage &&
          state.caseId === currentState.caseId
      );
      if (matchingState) matchingState.state = currentState.state;
      else formValidations.push(currentState);
      return formValidations;
    }, [] as FormValidation[])
  );

  private formValidityForCurrentStage$ = combineLatest([
    this.currentStageId$,
    this.collectiveFormValidation$,
    this.feaRecoveryCaseData$,
  ]).pipe(
    map(([currentStageId, formValidations, feaRecoveryData]) =>
      formValidations
        .filter(
          (validations) =>
            validations.stage === currentStageId &&
            validations.caseId === feaRecoveryData?.id
        )
        .every((validation) => validation.state)
    )
  );

  enableNextBtn$: Observable<boolean> = combineLatest([
    this._dcrpAuthorizationService.isAuthorizedFor('recovery-update'),
    this.feaRecoveryCaseData$,
    this.currentStageId$,
    this.formValidityForCurrentStage$,
  ]).pipe(
    map(
      ([isAuthorizedForRecoveryUpdate, recoveryData, stageId, formValid]) =>
        formValid &&
        isAuthorizedForRecoveryUpdate &&
        // TODO: Hardcoded case status (Close & AutoClose) needs to come from API with versioning related changes.
        recoveryData?.caseStatus != 6 &&
        recoveryData?.caseStatus != 7 &&
        (recoveryData?.region == this.PRS_REGION
          ? stageId != this.PRS_COMPLETE_STAGE
          : recoveryData?.region == this.PRN_REGION
            ? stageId != this.PRN_COMPLETE_STAGE
            : stageId != this.PRE_COMPLETE_STAGE)
    )
  );

  hideNextBtn$: Observable<boolean> = combineLatest([
    this._dcrpAuthorizationService.isAuthorizedFor('recovery-update'),
    this.feaRecoveryCaseData$,
    this.currentStageId$,
    this.shouldShowFeaRecoveryIssueInvoiceScreen$,
  ]).pipe(
    map(
      ([
        isAuthorizedForRecoveryUpdate,
        recoveryData,
        stageId,
        showInvoiceScreen,
      ]) => {
        let hiddenFlag: boolean = false;
        if (
          (stageId === 3 && !showInvoiceScreen) ||
          (stageId > 5 && recoveryData?.region == 'PRE') ||
          !isAuthorizedForRecoveryUpdate
        )
          hiddenFlag = true;
        return hiddenFlag;
      }
    )
  );

  FeaRecoveryBookingCargoDetails$: Observable<gcssBookingInfo | undefined> =
    this.feaRecoveryCaseData$.pipe(
      map((response) => {
        if (!response || !response.id) return undefined;
        return {
          invoiceNo: response.bolNumber,
          bookingNumber: response.bookingNumber,
        } as gcssBookingInfo;
      })
    );

  feaPreEpTradeData$: Observable<FeaPreEpTradeModel[]> = combineLatest([
    this.feaRecoveryCaseData$,
    this.reloadEpTradeData$,
  ]).pipe(
    switchMap(([recoveryData]) => {
      return this._feaPreService.feaPreCaseIdEpTradeDataGet(
        recoveryData?.id ?? 0
      );
    }),
    shareReplay(1)
  );

  feaPreEpTradePendingInfo$: Observable<{
    bookings: number;
    containers: number;
  }> = combineLatest([this.feaRecoveryCaseData$, this.feaPreEpTradeData$]).pipe(
    map(([recoveryData, finalEpTradeData]) => {
      return (
        finalEpTradeData
          // Filter out records that have an uploadedCaseId and do not match the current recovery case id
          .filter(
            (record) =>
              !!record.uploadedCaseId &&
              record.uploadedCaseId != recoveryData?.id
          )
          .reduce(
            (acc, record) => {
              // Count containers that are not 'SOC' in the current record
              const containerCount =
                record.feaPreEpTradeContainersCollection?.filter(
                  (a) => a.containerStatus !== 'SOC'
                ).length || 0;
              // If there are any such containers, increment bookings and containers counters
              if (containerCount > 0) {
                acc.bookings = acc.bookings + 1;
                acc.containers = acc.containers + containerCount;
              }
              // Return the accumulator for the next iteration
              return acc;
            },
            // Initial accumulator value
            { bookings: 0, containers: 0 }
          )
      );
    }),
    shareReplay(1)
  );

  uploadedEpTradeFilesForCase$ = this.reloadEpTradeUploadedFilesSubject$$.pipe(
    withLatestFrom(this.feaRecoveryCaseData$),
    switchMap(([_, recoveryCaseData]) => {
      if (!recoveryCaseData?.id) {
        return of(null);
      }
      return this._fileUploadService
        .fileUploadCaseIdUploadedFilesGet(recoveryCaseData.id)
        .pipe(
          catchError((error) => {
            // If there's an error, don't hide the component
            console.error('Error fetching uploaded files:', error);
            return of([]);
          })
        );
    }),
    shareReplay(1)
  );

  async saveDamageDetails(
    damages: TemplateModel[],
    caseId: number,
    caseCurrency: string
  ): Promise<boolean> {
    const damageDetailRequest: CaseDamageDetails[] = [];
    const currentItems = damages;
    const caseCurrencyAndExchangeRate = await firstValueFrom(
      this.caseCurrencyAndExchangeRate$
    );
    currentItems?.forEach((damage: TemplateModel) => {
      if (
        !damage.hidden &&
        damage.items &&
        this._sharedDataService.validateDamageLineItem(damage.items)
      ) {
        const caseAmount = damage.items?.filter(
          (i) => i.name == 'amountInCaseCurrency'
        )[0].value;
        damageDetailRequest.push({
          damageDescription: damage.items?.filter(
            (i) => i.name == 'description'
          )[0].value,
          materialCodeId: damage.items?.filter(
            (i) => i.name == 'materialCode'
          )[0].value,
          id: damage.items?.filter((i) => i.name == 'id')[0].value,
          caseId: caseId,
          damageAmountInCaseCurrency: damage.items?.filter(
            (i) => i.name == 'amountInCaseCurrency'
          )[0].value,
          damageAmountInUsd:
            Math.round(
              caseAmount *
                100 *
                (caseCurrencyAndExchangeRate?.exchangeRatePerUnit ?? 0)
            ) / 100,
          caseCurrency: caseCurrency,
        });
      }
    });

    const saveSuccess = await lastValueFrom(
      this._damageService
        .caseDamageDetailsDeleteAndInsertPut(damageDetailRequest)
        .pipe(map((response) => response.isSuccess ?? false))
    );
    if (saveSuccess) this.reloadDamageDetails();
    return saveSuccess;
  }

  /**
   * Save damage details for a given PRE case.
   * @param caseId The ID of the case.
   * @param caseCurrency The currency of the case.
   * @returns A promise that resolves to a boolean indicating success.
   */
  async savePreDamageDetails(
    caseId: number,
    caseCurrency: string
  ): Promise<boolean> {
    const damageDetailRequest: CaseDamageDetails[] = [];
    const caseCurrencyAndExchangeRate = await firstValueFrom(
      this.caseCurrencyAndExchangeRate$
    );
    const feaPreEpTradeData = await firstValueFrom(this.feaPreEpTradeData$);

    if (feaPreEpTradeData && Array.isArray(feaPreEpTradeData)) {
      // For each item in feaPreEpTradeData$, create a CaseDamageDetails object and push to damageDetailRequest
      feaPreEpTradeData.forEach((epTradeData) => {
        if (
          epTradeData.aboveCoverageCost &&
          epTradeData.aboveCoverageCost > 0
        ) {
          damageDetailRequest.push({
            damageDescription: `BL - ${epTradeData.bolNumber ?? ''} repair cost`,
            materialCodeId: 103295,
            id: undefined,
            caseId: caseId,
            damageAmountInCaseCurrency: epTradeData.aboveCoverageCost,
            damageAmountInUsd:
              Math.round(
                epTradeData.aboveCoverageCost *
                  100 *
                  (caseCurrencyAndExchangeRate?.exchangeRatePerUnit ?? 0)
              ) / 100,
            caseCurrency: caseCurrency,
          });
        }
      });
    }

    const saveSuccess = await lastValueFrom(
      this._damageService
        .caseDamageDetailsDeleteAndInsertPut(damageDetailRequest)
        .pipe(map((response) => response.isSuccess ?? false))
    );
    return saveSuccess;
  }

  onResendEmailClicked(mail: SendMailRequest): Observable<boolean> {
    if (!mail || !mail.liabilityLetter) return of(false);
    const emailSendGridSendRequest = {
      to: mail.to,
      cc: mail.cc,
      from: mail.from,
      body: mail.body,
      subject: mail.subject,
      emailAttachments: mail.emailAttachment,
    } as SendGridEmail;

    const emailAPISaveRequest = {
      recoveryCaseId: mail.liabilityLetter.recoveryCaseId,
      liablePartyName: mail.liabilityLetter.liabilityPartyName,
      liabilityAmount: mail.liabilityLetter.liabilityAmount,
      liabilityLetterCaseType: 'Fea',
      liabilityLetterResponseReason: '',
      emailBlobPath: '',
      to: emailSendGridSendRequest.to,
      cc: emailSendGridSendRequest.cc,
      subject: emailSendGridSendRequest.subject,
      body: emailSendGridSendRequest.body,
      emailAttachments: emailSendGridSendRequest.emailAttachments,
    } as SaveLiabilityLetter;

    const mailUploadReq = {
      caseId: mail.liabilityLetter.recoveryCaseId,
      emailType: 'Vendor_Notification_Email',
      fileName: '',
      recoveryCaseType: 'Fea',
      from: emailSendGridSendRequest.from,
      to: emailSendGridSendRequest.to,
      cc: emailSendGridSendRequest.cc,
      subject: emailSendGridSendRequest.subject,
      body: emailSendGridSendRequest.body,
      emailAttachments: emailSendGridSendRequest.emailAttachments,
    } as UploadEmailRequest;

    return this._notificationService
      .notificationUploadEmailPost(mailUploadReq)
      .pipe(
        switchMap((mailUploadURI) =>
          forkJoin({
            mailSend: this._notificationService
              .notificationSendEmailPost(
                emailSendGridSendRequest,
                this.apiVersion
              )
              .pipe(map((response) => response as number)),
            mailSave: this._liabilityService.liabilityLetterPost(
              { ...emailAPISaveRequest, emailBlobPath: mailUploadURI },
              this.apiVersion
            ),
          })
        ),
        map(() => true),
        tap(() => this.reloadLiabilityLetters()),
        catchError(() => of(false))
      );
  }

  async updateLiabilityLetter(
    selectedLetter: LiabilityLetter,
    liabilityLetterStatus: LiabilityLetterStatus
  ): Promise<boolean> {
    const recoveryDataCaseID =
      (await firstValueFrom(this.feaRecoveryCaseData$))?.id ?? 0;
    const request = {
      liabilityLetterStatus: liabilityLetterStatus,
      liabilityLetterResponseReason:
        selectedLetter.liabilityLetterResponseReason,
      recoveryCaseId: recoveryDataCaseID,
    } as UpdateLiabilityLetterStatusVendor;

    await lastValueFrom(
      this._liabilityLetterService
        .liabilityLetterIdPut(request, selectedLetter.id ?? 0)
        .pipe(
          catchError(() => {
            return of(false);
          })
        )
    );
    return true;
  }

  updateUnSelectLinkedCases(lineItems: number[]) {
    this.unSelectedLinkedCases$$.next(lineItems);
  }

  updateSelectLinkedCases(lineItems: number[]) {
    this.selectedLinkedCases$$.next(lineItems);
  }

  updateLinkedCasesLineItems(lineItem: RecoveryCase[]) {
    this.linkedCases$$.next(lineItem);
  }

  downloadLetter(emailBlobPath: string, _caseNumber: string): Observable<Blob> {
    return this._blobService.blobDownloadGet(emailBlobPath);
  }

  reloadEpTradeData(): void {
    this.reloadEpTradeDataSubject$$.next(true);
  }

  damageDetails$: Observable<CaseDamageDetailDto[] | undefined> = combineLatest(
    [this.feaRecoveryCaseData$, this.reloadDamageDetails$$.asObservable()]
  ).pipe(
    switchMap(([feaRecoveryData]) => {
      if (!feaRecoveryData || !feaRecoveryData.id) return of(undefined);
      return this._damageService
        .caseDamageDetailsByCaseIdCaseIdGet(feaRecoveryData.id)
        .pipe(
          map((response) => {
            if (!response || !response.data) return [];
            return response.data.map(
              (damage) =>
                ({
                  description: damage.damageDescription,
                  id: damage.id,
                  materialCode: damage.materialCodeId,
                  amount: damage.damageAmountInUsd,
                  amountInCaseCurrency: damage.damageAmountInCaseCurrency,
                  caseId: damage.caseId,
                  caseCurrency: damage.caseCurrency,
                }) as CaseDamageDetailDto
            );
          })
        );
    }),
    shareReplay(1)
  );

  recoveryCaseOverview$: Observable<RecoveryCaseOverview | undefined> =
    combineLatest([this.feaRecoveryCaseData$]).pipe(
      switchMap(([feaRecoveryData]) => {
        if (!feaRecoveryData || !feaRecoveryData.id) return of(undefined);
        return this._recoveryClaimService.recoveryCasesCaseIdOverviewGet(
          feaRecoveryData.id,
          RecoveryCaseType.Fea
        );
      }),
      shareReplay(1)
    );

  shouldShowFeaRecoveryLiabilityDetails$: Observable<boolean> = combineLatest([
    this.shouldShowLiabilityDetailsSubject$$.asObservable(),
    this.shouldShowFeaRecoveryIssueInvoiceScreen$,
    this.feaRecoveryLiabilityLetters$,
    this.feaRecoveryCaseData$,
  ]).pipe(
    map(
      ([
        shouldShowLiabilityDetails,
        shouldShowIssueInvoiceScreen,
        liabilityLetters,
        caseData,
      ]) => {
        if (caseData?.region === 'PRE') {
          return shouldShowIssueInvoiceScreen;
        }
        return (
          shouldShowLiabilityDetails ||
          !liabilityLetters ||
          liabilityLetters.length === 0
        );
      }
    ),
    shareReplay(1)
  );

  feaRecoveryLiablePartyData$: Observable<LiabilityParty | undefined> =
    combineLatest([
      this.feaRecoveryCaseData$,
      this.reloadLiabilityPartyDetails$$.asObservable(),
    ]).pipe(
      switchMap(([feaRecoveryData]) => {
        if (!feaRecoveryData?.id) return of(undefined);
        return this._liabilityPartyService
          .liabilityPartiesGet(feaRecoveryData.id ?? 0, this.apiVersion)
          .pipe(
            map((response) => {
              if (!response || !response.data) return;
              return response?.data as LiabilityParty;
            }),
            catchError((_) => {
              return of(undefined);
            })
          );
      })
    );

  resetCaseContext() {
    this.currentStageId$$.next(1);
    this.workFlowStageId$$.next(1);
    this.reloadFeaRecoveryData$$.next(false);
    this.formValidity$$.next(null);
  }

  updateFormValidationState(state: FormValidation) {
    this.formValidity$$.next(state);
  }

  reloadDamageDetails(): void {
    this.reloadDamageDetails$$.next(true);
  }

  updateLiabilityDetailsVisibility(value: boolean) {
    this.shouldShowLiabilityDetailsSubject$$.next(value);
  }

  reloadRecoveryCaseData(): void {
    this.reloadFeaRecoveryData$$.next(true);
  }

  resetRecoveryCaseData(): void {
    this.reloadFeaRecoveryData$$.next(false);
  }

  updateCurrentStageId(stageId: number): void {
    this.currentStageId$$.next(stageId);
  }

  updateTabSelected(tabIndex: number) {
    this.tabIndex$$.next(tabIndex);
  }

  updateContainerDetails(
    containerDetails: ContainerMoveDto | null | undefined
  ) {
    this.containerDetails$$.next(containerDetails);
  }

  reloadEpTradeUploadedFiles() {
    this.reloadEpTradeUploadedFilesSubject$$.next(true);
  }

  // TODO: Temporary fix will be removed with case versioning changes.
  getCaseStatusByStageID(stageID: number, region: FeaRegion): number {
    let caseStatus = 1; // New
    if (region == 'PRS') {
      switch (stageID) {
        case 1:
          caseStatus = 1; // New
          break;
        case 2:
          caseStatus = 3; // Liability
          break;
        case 3:
          caseStatus = 4; // Invoice
          break;
        case 4:
          caseStatus = 5; // Complete
          break;
      }
    }
    if (region == 'PRN') {
      switch (stageID) {
        case 1:
          caseStatus = 8; // Cost Comparison
          break;
        case 2:
          caseStatus = 9; // 3P cost
          break;
        case 3:
          caseStatus = 3; // liability
          break;
        case 4:
          caseStatus = 4; // Invoice
          break;
        case 5:
          caseStatus = 5; // Complete
          break;
      }
    }
    if (region == 'PRE') {
      switch (stageID) {
        case 1:
          caseStatus = 8; // Cost Comparison
          break;
        case 2:
          caseStatus = 10; // Container/BL Match
          break;
        case 3:
          caseStatus = 11; // Booking
          break;
        case 4:
          caseStatus = 9; // 3P cost
          break;
        case 5:
          caseStatus = 4; // Invoice
          break;
        case 6:
          caseStatus = 5; // Complete
          break;
      }
    }
    return caseStatus;
  }

  caseDocumentGet(
    caseId: number
  ): Observable<ClaimReferenceDocumentDto | null> {
    return this._recoveryClaimService
      .recoveryCasesCaseIdAttachmentsGet(caseId, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return null;
          return response.data;
        })
      );
  }

  caseDocumentPost(
    imagesAssignedToCases: ClaimReferenceDocumentDto
  ): Observable<ClaimReferenceDocumentDto | null> {
    return this._recoveryClaimService
      .recoveryCasesCaseIdAttachmentsPost(
        imagesAssignedToCases.caseId ?? 0,
        this.apiVersion,
        { claimReferenceDocumentDto: imagesAssignedToCases }
      )
      .pipe(
        map((response) => {
          if (!response || !response.data) return null;
          return response.data;
        })
      );
  }

  caseDocumentDelete(documentId: number): Observable<boolean> {
    return this._recoveryCaseDocumentService
      .caseDocumentsIdDelete(documentId, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return false;
          return response.data;
        })
      );
  }

  inspectionsGet(
    _caseNumber: string,
    containerNumber: string | undefined,
    workOrderNumber: number | undefined,
    _incidentDate: Date | null,
    workOrderDate: Date | null
  ): Observable<Array<Inspection> | null> {
    let dateToFetchInspections = new Date();
    const toDatePlusVal = environment.searchIntervalForInspections;

    if (workOrderDate && workOrderDate.toString() != '0001-01-01T00:00:00') {
      dateToFetchInspections = new Date(workOrderDate);
    }

    const fromDate = new Date(dateToFetchInspections.getTime());
    fromDate.setUTCDate(fromDate.getUTCDate() - toDatePlusVal);

    const toDate = new Date(dateToFetchInspections.getTime());
    toDate.setUTCDate(toDate.getUTCDate() + toDatePlusVal);

    return this._recoveryInspectionService
      .inspectionsGet(
        containerNumber,
        fromDate,
        toDate,
        workOrderNumber && workOrderNumber > 500000000
          ? workOrderNumber
          : undefined,
        undefined,
        this.apiVersion
      )
      .pipe(
        map((response) => {
          if (!response || !response.data) return null;
          return response.data;
        })
      );
  }

  saveImageAvailability(
    _inspectionInfo: InspectionDetailsDTO,
    _caseNumber: string
  ): Observable<boolean> {
    return of(true);
  }

  async saveComment(comment: string): Promise<boolean> {
    const feaRecoveryCaseData = await firstValueFrom(this.feaRecoveryCaseData$);
    if (!feaRecoveryCaseData) return false;
    feaRecoveryCaseData.comment = comment;
    const response = await firstValueFrom(
      this._recoveryClaimService
        .recoveryCasesCaseIdPut(
          feaRecoveryCaseData.id ?? 0,
          feaRecoveryCaseData
        )
        .pipe(
          map((response) => {
            if (!response || !response.data) return false;
            return true;
          }),
          catchError((_) => {
            return of(false);
          })
        )
    );
    return response;
  }

  updateOverviewDetails(overview: TemplateModel[]) {
    this.overviewDetails$$.next(overview);
  }

  getActivityLogs(caseId: number): Observable<CaseActivityLogsDtoListResponse> {
    return this._recoveryClaimService.recoveryCasesCaseIdActivityLogsGet(
      caseId,
      RecoveryCaseType.Fea,
      this.apiVersion
    );
  }

  reloadLiabilityPartyDetails() {
    this.reloadLiabilityPartyDetails$$.next(true);
  }

  reloadLiabilityLetters() {
    this.reloadLiabilityLetters$$.next(true);
  }

  reloadInvoices() {
    this.reloadInvoices$$.next(true);
  }

  reloadBulkInvoices() {
    this.reloadBulkInvoices$$.next(true);
  }

  updateIssueInvoiceVisibility(value: boolean) {
    this.shouldShowIssueInvoiceScreenSubject$$.next(value);
  }

  postInvoiceDetails(
    createInvoice: CreateInvoice,
    _userId: string
  ): Observable<boolean> {
    return this._invoiceDetailService
      .invoiceDetailsPost(createInvoice, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return false;
          return response.data;
        })
      );
  }

  putInvoiceDetails(
    invoiceId: number,
    updateInvoice: UpdateInvoice,
    _userId: string
  ): Observable<boolean> {
    return this._invoiceDetailService
      .invoiceDetailsIdPut(invoiceId, updateInvoice, this.apiVersion)
      .pipe(
        map((response) => {
          if (!response || !response.data) return false;
          return true;
        })
      );
  }

  saveAttachment(
    _caseNumber: string,
    _fromDateTime?: Date | null,
    _toDateTime?: Date | null
  ): Promise<boolean> {
    throw new Error('Method not implemented.');
  }

  updateCustomerData(data: CustomerInfoDto): void {
    this.customerDataSubject$$.next(data);
  }

  updateCaseCurrencyAndExchangeRate(
    currencyAndExchangeRate: CurrencyAndExchangeRateDto | undefined
  ): void {
    this.caseCurrencyAndExchangeRate$$.next(currencyAndExchangeRate);
  }

  updateFactCode(factCode: string | undefined): void {
    this.selectedFactCode$$.next(factCode);
  }
}
